import { createContext, useContext, useMemo, type FunctionComponent } from 'react'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import type { API } from '../../pages/api/v1/[handler]'
import type { SemanticKeyResult } from '../data/SemanticKeyFragment'

export const TranslationContext = createContext<Record<string, string | undefined>>({})

export const TranslationRoot: FunctionComponent<{
	semanticKeys: SemanticKeyResult[]
	children: React.ReactNode
}> = ({ children, semanticKeys }) => {
	const value = useMemo(() => {
		return Object.fromEntries(semanticKeys.map((key) => [key.key, key.value] as const))
	}, [semanticKeys])
	return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>
}

export function useTranslationContext() {
	return useContext(TranslationContext)
}

console.log('NEXT_PUBLIC_AUTOREGISTER_SEMANTIC_KEYS', process.env.NEXT_PUBLIC_AUTOREGISTER_SEMANTIC_KEYS)

const handleMissingTranslation = process.env.NEXT_PUBLIC_AUTOREGISTER_SEMANTIC_KEYS
	? async (key: string) => {
			if (typeof window !== 'undefined') {
				const alreadyRegistered = localStorage.getItem(`semanticKeyRegistered:${key}`)
				if (!alreadyRegistered) {
					localStorage.setItem(`semanticKeyRegistered:${key}`, 'true')
					return createFetcher<API>('/api/v1/[handler]')('registerSemanticKey', { key })
				}
			}
		}
	: null

export function useTranslate() {
	const translations = useTranslationContext()

	return (key: string, fallback?: string) => {
		const result = translations[key] ?? `[${fallback ?? key}]`

		if (typeof translations[key] !== 'string' && handleMissingTranslation) {
			handleMissingTranslation(key)
		}

		return result
	}
}

export function useTranslation(key: string, fallback?: string) {
	const translate = useTranslate()
	return translate(key, fallback)
}

export const Translation: FunctionComponent<{ tr: string; fallback?: string }> = ({ tr, fallback }) => {
	return <>{useTranslation(tr, fallback)}</>
}
